import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useConfigMapJson } from "@shared/api/hooks";
import { useUpdateUnit } from "../../../../hook";

interface UpdateUnitModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resource: any;
  onSuccess?: (premise: string, unit: string) => void;
}

export const UpdateUnitModal: React.FC<UpdateUnitModalProps> = ({
  open,
  onOpenChange,
  resource,
  onSuccess,
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const [watchPremise, setWatchPremise] = useState<string>("");
  const updateUnitMutation = useUpdateUnit();

  const { data: premiseConfig } = useConfigMapJson(
    "territory_json",
    remoteUtilityId,
    "premise"
  );
  const { data: unitConfig } = useConfigMapJson(
    "territory_json",
    remoteUtilityId,
    "unit",
    watchPremise || undefined
  );

  const premiseOptions = useMemo(() => {
    return Array.isArray(premiseConfig)
      ? premiseConfig.map((item: any) => ({
          label: item.name || item,
          value: item.code || item,
        }))
      : [];
  }, [premiseConfig]);

  const unitOptions = useMemo(() => {
    return Array.isArray(unitConfig)
      ? unitConfig.map((item: any) => ({
          label: item.name || item,
          value: item.code || item,
        }))
      : [];
  }, [unitConfig]);

  const formFields: FormField[] = [
    {
      name: "premise",
      type: "select",
      required: true,
      label: "Premise",
      placeholder: "Select premise",
      options: premiseOptions,
      helperText: "The property or location where this resource is physically placed.",
    },
    {
      name: "unit",
      type: "select",
      required: true,
      label: "Unit",
      placeholder: "Select unit",
      options: unitOptions,
      helperText: "The specific unit within the selected premise to assign this resource to.",
      disabled: (formValues) => !formValues.premise,
      className:"mt-4"
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
  });

  const currentPremise = form.watch("premise");
  useEffect(() => {
    setWatchPremise(currentPremise || "");
  }, [currentPremise]);

  // Reset when opening
  useEffect(() => {
    if (open) {
      form.reset({
         premise: resource?.premise,
        unit: resource?.unitCode,
      });
    }
  }, [open]);

  const handleSubmit = async (data: any) => {
    if (resource?.id) {
      try {
        await updateUnitMutation.mutateAsync({
          resourceId: resource.id,
          payload: {
            unit_code: data.unit,
            premise: data.premise,
          },
        });
        if (onSuccess) {
          onSuccess(data.premise, data.unit);
        }
        onOpenChange(false);
      } catch (error) {
        console.error("Failed to update unit", error);
      }
    }
  };

  const isUpdating = updateUnitMutation.isPending;

  const renderCustomFooter = () => (
    <div className="flex justify-end gap-3 mt-6">
      <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isUpdating}>
        Cancel
      </Button>
      <Button type="submit" disabled={!form.formState.isValid || isUpdating}>
        {isUpdating ? "Saving..." : "Save"}
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Update Unit</DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            Select a premise and unit for resource <span className="font-semibold">{resource?.id}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            config={{
              showSubmitButton: false,
              classes:FIELD_CLASSES
            }}
            renderCustomFooter={renderCustomFooter}
            gridColumns={1}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
